import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import queryString from 'query-string'
import { useAutoLogin, useLoginSuccess } from './useAutoLogin'
import {
  getFitonToken,
  getLocalLanguage,
  getStateId,
  setFitonToken,
  setFlow,
} from 'utils/authLocalStorage'
import { fitonGetProfilePromise } from 'apis/FitOnAuthAPI'
import _ from 'lodash'
import { ScreenKey } from 'views/Care/util'
import { useDeeplink } from './useDeeplink'
import { logAmplitudeEvent, setAmplitudeProfile } from 'utils/amplitudeInit'
import { setBrazeProfile } from 'utils/brazeInit'
import { useFeatureFlagByName } from 'contexts/FeatureFlagContext'
import {
  useClearUserInfo,
  useRehabSelectedThemes,
  useStateList,
  useSkipToScheduledTime,
  useUnavailableStatus,
  useUhOhStatus,
  useRehabQuestion2,
} from 'contexts/AuthContext'
import { getWebFitOnHost } from 'apis/formatUrl'
import { getSkuThemesPromise, getStateListPromise } from 'apis/FitOnCareAPI'
import { useUserProfileStatus } from './useUserProfileStatus'
import { UnavailableStatus } from 'views/Care/Unavailable'
import { UhOhStatus } from 'views/Care/UhOh'

export const SkuNames = ['wlc', 'msk', 'pt', 'rd', 'mca']
export const SkuNamesWithoutRD = ['wlc', 'msk', 'pt', 'mca']

const useMCAFlowEnabled = () => {
  const { flags, loaded } = useFeatureFlagByName({ keys: ['mca_flow_enabled'] })
  return {
    mcaFlowFlagEnabled: flags.find((flag) => flag.key === 'mca_flow_enabled')
      ?.enabled,

    mcaFlowFlagLoaded: loaded,
  }
}

export const useFlow = ({
  supportForNewWelcome,
  initialKey,
  openInitialScreen = true,
} = {}) => {
  const navigate = useNavigate()
  const IgnoredKey = [ScreenKey.VERIFY_EMAIL, ScreenKey.REHAB_CONGRATULATIONS]
  const [screenList, setScreenList] = useState([])
  const [currentKey, setCurrentKey] = useState()
  const [isLoadingTheme, setIsLoadingTheme] = useState(false)
  let { ...params } = queryString.parse(window.location.search)
  const { unavailableStatus } = useUnavailableStatus()
  const { uhOhStatus } = useUhOhStatus()
  const { rehabQuestion2 } = useRehabQuestion2()
  const [isAutoLoginDataLoading, setIsAutoLoginDataLoading] = useState(false)
  const { mcaFlowFlagEnabled, mcaFlowFlagLoaded } = useMCAFlowEnabled()
  const {
    email,
    utmCampaign,
    utmContent,
    utmSource,
    landing,
    variant,
    promo,
    fitonUserId,
    theme,
    programId,
    from,
    gender,
    telehealth,
    prescreen,
    t,
    h,
  } = useDeeplink()

  const sku = useMemo(() => {
    return SkuNames.includes(variant) ? variant : 'rd'
  }, [variant])

  const { isClearAll, setIsClearAll } = useClearUserInfo()
  const {
    shouldSkipWLCScheduledTime,
    shouldSkipMSKScheduledTime,
    getUserProfileStatus,
  } = useUserProfileStatus()
  const { setRehabSelectedThemes, rehabSelectedThemes } =
    useRehabSelectedThemes()
  const { skipToScheduledTime, setSkipToScheduledTime } =
    useSkipToScheduledTime()
  const { setStateList, getStateById } = useStateList()
  const { openAutoLoginScreen } = useLoginSuccess({
    sku,
    supportForNewWelcome,
    setCurrentKey,
    fitonEmail: email,
    email,
    utmSource,
  })
  const {
    autoLoginLoading,
    autoLoginSuccess,
    onLoginSuccess,
    getHealthieUser,
    setAutoLoginSuccess,
    autoLoginErrorCode,
    handleUserAutoLogin,
  } = useAutoLogin({
    fitonUserId,
    sku,
    supportForNewWelcome,
    email,
    t,
    h,
    utmCampaign,
    utmContent,
    utmSource,
    rehabSelectedThemes,
  })

  const shouldSkipScheduledTime = useMemo(() => {
    return (
      (telehealth === 'false' && (sku === 'msk' || sku === 'wlc')) ||
      (sku === 'wlc' && shouldSkipWLCScheduledTime) ||
      (sku === 'msk' && shouldSkipMSKScheduledTime)
    )
  }, [telehealth, sku, shouldSkipWLCScheduledTime, shouldSkipMSKScheduledTime])

  const getInitialScreen = async (variant) => {
    //If you want to test the MCA assessment screen, uncomment the following line
    // return { key: ScreenKey.MCA_ASSESSMENT, list: [ScreenKey.MCA_ASSESSMENT] }
    if (initialKey) {
      return { key: initialKey, list: [initialKey] }
    }

    if (variant === 'mca') {
      if (!mcaFlowFlagEnabled) {
        return {
          key: ScreenKey.GENERIC_SCREEN,
          list: [ScreenKey.GENERIC_SCREEN],
        }
      }
      return { key: ScreenKey.WELCOME, list: [ScreenKey.WELCOME] }
    }
    const { shouldSkipFlow } = await getUserProfileStatus()
    if (variant) {
      if (variant === 'wlc') {
        return { key: ScreenKey.WELCOME, list: [ScreenKey.WELCOME] }
      }
      if (variant === 'msk') {
        if (shouldSkipFlow) {
          if (_.isEmpty(rehabSelectedThemes)) {
            return {
              key: ScreenKey.PAIN_LOCATED,
              list: [ScreenKey.WELCOME, ScreenKey.PAIN_LOCATED],
            }
          }
          return {
            key: ScreenKey.REHAB_QUESTION_1,
            list: [ScreenKey.WELCOME, ScreenKey.REHAB_QUESTION_1],
          }
        }
        return { key: ScreenKey.WELCOME, list: [ScreenKey.WELCOME] }
      }
      if (
        variant === 'prescreen' ||
        (['pt', 'rd'].includes(variant) && prescreen === 'true')
      ) {
        return {
          key: ScreenKey.PRESCREEN_QUESTIONS,
          list: [ScreenKey.PRESCREEN_QUESTIONS],
        }
      }
      if (landing === 'true' || variant === 'pt') {
        return shouldSkipFlow
          ? {
              key: ScreenKey.PERSONAL_INFO,
              list: [ScreenKey.WELCOME, ScreenKey.PERSONAL_INFO],
            }
          : { key: ScreenKey.WELCOME, list: [ScreenKey.WELCOME] }
      }
      return { key: ScreenKey.PERSONAL_INFO, list: [ScreenKey.PERSONAL_INFO] }
    } else if (prescreen === 'true') {
      return {
        key: ScreenKey.PRESCREEN_QUESTIONS,
        list: [ScreenKey.PRESCREEN_QUESTIONS],
      }
    }

    if (landing === 'true') {
      return shouldSkipFlow
        ? {
            key: ScreenKey.PERSONAL_INFO,
            list: [ScreenKey.WELCOME, ScreenKey.PERSONAL_INFO],
          }
        : { key: ScreenKey.WELCOME, list: [ScreenKey.WELCOME] }
    }
    return { key: ScreenKey.PERSONAL_INFO, list: [ScreenKey.PERSONAL_INFO] }
  }

  const getShouldSkipScheduledTime = async () => {
    const userProfile = await getUserProfileStatus()
    const { shouldSkipWLCScheduledTime, shouldSkipMSKScheduledTime } =
      userProfile
    return (
      (telehealth === 'false' && (sku === 'msk' || sku === 'wlc')) ||
      (sku === 'wlc' && shouldSkipWLCScheduledTime) ||
      (sku === 'msk' && shouldSkipMSKScheduledTime)
    )
  }

  const navigateToAboutYou = async () => {
    if (getFitonToken()) {
      const resp = await fitonGetProfilePromise().catch(() => null)
      const profileId = resp?.data?.id
      if (profileId && String(profileId) !== String(fitonUserId)) {
        setFitonToken('')
      }
    }
    navigate('/about-you', {
      state: {
        fitonUserId,
        fitonEmail: email,
        variant,
        gender,
        t,
        h,
      },
    })
  }

  const getInitialScreenAsync = useCallback(async () => {
    if (autoLoginSuccess) {
      setIsAutoLoginDataLoading(true)
      const state = getStateById(getStateId())
      const shouldSkipScheduledTime = await getShouldSkipScheduledTime()
      const key = await openAutoLoginScreen(
        state,
        sku,
        theme,
        shouldSkipScheduledTime
      )
      if (key === ScreenKey.MCA_ABOUT_YOU) {
        await navigateToAboutYou()
        setIsAutoLoginDataLoading(false)
        return
      }
      setCurrentKey(key)
      setScreenList([key])
      setIsAutoLoginDataLoading(false)
    } else if (!autoLoginLoading) {
      if (+autoLoginErrorCode === 404 && sku === 'mca') {
        setIsAutoLoginDataLoading(true)
        await navigateToAboutYou()
        setIsAutoLoginDataLoading(false)
      } else {
        const { key, list } = await getInitialScreen(variant)
        setCurrentKey(key)
        setScreenList(list)
      }

      if (variant && SkuNamesWithoutRD.includes(variant)) {
        logUTMEvents({ sku: variant })
      } else if (variant === 'prescreen') {
        logUTMEvents({
          type: 'Prescreen Questions',
          sku: sku,
        })
      } else if (landing === 'true') {
        logUTMEvents({ sku: sku })
      }
      updateUTMProfile()
    }
  }, [
    landing,
    autoLoginLoading,
    autoLoginSuccess,
    variant,
    theme,
    sku,
    autoLoginErrorCode,
    mcaFlowFlagLoaded,
    mcaFlowFlagEnabled,
  ])

  useEffect(() => {
    if ((!mcaFlowFlagLoaded && variant === 'mca') || !openInitialScreen) return
    getInitialScreenAsync()
  }, [getInitialScreenAsync, mcaFlowFlagLoaded, openInitialScreen, variant])

  const gotoLoginIfNeeded = (variant) => {
    switch (variant) {
      case 'wlc':
        if (_.isEmpty(fitonUserId) || _.isEmpty(email)) {
          const queryParams = { ...params, r: 'wlc-signup' }
          window.location.href = `${getWebFitOnHost()}/?${queryString.stringify(
            queryParams
          )}`
        }
        break
      case 'msk':
        if (_.isEmpty(fitonUserId) || _.isEmpty(email)) {
          const queryParams = { ...params, r: 'msk-signup', theme: theme }
          window.location.href = `${getWebFitOnHost()}/?${queryString.stringify(
            queryParams
          )}`
        }
        break
      default:
        break
    }
  }

  useEffect(() => {
    if (variant) {
      gotoLoginIfNeeded(variant)
    }
  }, [variant])

  useEffect(() => {
    setAmplitudeProfile({ promoCode: promo })
    setBrazeProfile({ promoCode: promo })
  }, [promo])

  useEffect(() => {
    if (!_.isEmpty(screenList) && !SkuNamesWithoutRD.includes(variant)) {
      setFlow(screenList)
    }
  }, [screenList, variant])

  useEffect(() => {
    if (isClearAll) {
      const { key, list } = getInitialScreen(variant)
      setCurrentKey(key)
      setScreenList(list)
      setIsClearAll(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClearAll, variant])

  const updateUTMProfile = () => {
    setAmplitudeProfile({ utmSource, utmCampaign, utmContent })
    setBrazeProfile({ utmSource, utmCampaign, utmContent })
  }

  const logUTMEvents = ({ type, ...otherParams } = {}) => {
    const params = {}
    if (utmSource) {
      params.utm_source = utmSource
    }
    if (utmCampaign) {
      params.utm_campaign = utmCampaign
    }
    if (utmContent) {
      params.utm_content = utmContent
    }
    params.language = getLocalLanguage()
    logAmplitudeEvent('Signup Start', { ...params, ...otherParams })
  }

  const setNewScreenKey = (key, isBack = false) => {
    const index = screenList.indexOf(key)
    if (index > 0) {
      setScreenList(screenList.slice(0, index + 1))
    } else {
      if (index === 0) {
        setScreenList([key])
      } else if (!isBack && key !== ScreenKey.UNAVAILABLE) {
        setScreenList([...screenList, key])
      }
    }
    if (key) {
      setCurrentKey(key)
    }
    window.scrollTo(0, 0)
  }

  const getScreenList = ({ shouldSkipFlow, userProfile } = {}) => {
    if (shouldSkipFlow) {
      switch (sku) {
        case 'msk':
          return [
            ScreenKey.WELCOME,
            theme ? ScreenKey.REHAB_QUESTION_1 : ScreenKey.PAIN_LOCATED,
            ScreenKey.REHAB_QUESTION_2,
            ScreenKey.REHAB_CONSULT_PROVIDER,
            ScreenKey.PERSONAL_INFO,
            ScreenKey.COMPLETE_PROFILE,
            ...(utmSource ? [] : [ScreenKey.HEAR_ABOUT]),
            ScreenKey.TALK_ABOUT_COVERAGE,
            ScreenKey.INSURANCE_CARD_INFO,
            ScreenKey.INSURANCE_HOME_ADDRESS,
            ...(shouldSkipScheduledTime ? [] : [ScreenKey.SCHEDULED_TIME]),
            ScreenKey.REHAB_CONGRATULATIONS,
          ]
        case 'wlc':
          return [
            ScreenKey.WELCOME,
            ScreenKey.PERSONAL_INFO,
            ScreenKey.COMPLETE_PROFILE,
            ...(utmSource ? [] : [ScreenKey.HEAR_ABOUT]),
            ScreenKey.PAST_DIAGNOSIS,
            ScreenKey.TALK_ABOUT_COVERAGE,
            ScreenKey.INSURANCE_CARD_INFO,
            ScreenKey.INSURANCE_HOME_ADDRESS,
            ...(shouldSkipScheduledTime ? [] : [ScreenKey.SCHEDULED_TIME]),
            ScreenKey.SKU_CONGRATULATIONS,
          ]
        case 'pt':
          return [
            ScreenKey.WELCOME,
            ScreenKey.PERSONAL_INFO,
            ScreenKey.COMPLETE_PROFILE,
            ...(utmSource ? [] : [ScreenKey.HEAR_ABOUT]),
            ScreenKey.PAIN_LOCATED,
            ...(_.isEmpty(rehabQuestion2) ? [] : [ScreenKey.REHAB_QUESTION_2]),
            ScreenKey.TALK_ABOUT_COVERAGE,
            ScreenKey.INSURANCE_CARD_INFO,
            ScreenKey.INSURANCE_HOME_ADDRESS,
            ScreenKey.SCHEDULED_TIME,
          ]
        case 'mca':
          return screenList
        default:
          const { insuranceProviderOptionId, helpMostOptionId } =
            userProfile?.userSelectOptions || {}

          const baseScreens = [
            ScreenKey.WELCOME,
            ScreenKey.PERSONAL_INFO,
            ScreenKey.SURVEY,
            ...(+helpMostOptionId === 3 ? [ScreenKey.HORMONAL_AUTOIMMUNE] : []),
            ScreenKey.COMPLETE_PROFILE,
            ...(utmSource ? [] : [ScreenKey.HEAR_ABOUT]),
            ScreenKey.TALK_ABOUT_COVERAGE,
          ]

          switch (+insuranceProviderOptionId) {
            case 5:
              return [
                ...baseScreens,
                ScreenKey.CASH_PAY,
                ScreenKey.SCHEDULED_TIME,
              ]

            case 6:
              return [
                ...baseScreens,
                ScreenKey.INSURANCE_NOT_ACCEPTED,
                ScreenKey.CASH_PAY,
                ScreenKey.SCHEDULED_TIME,
              ]

            default:
              return [
                ...baseScreens,
                ScreenKey.INSURANCE_CARD_INFO,
                ScreenKey.INSURANCE_HOME_ADDRESS,
                ScreenKey.SCHEDULED_TIME,
              ]
          }
      }
    }
    return screenList
  }

  const getBackKey = async (key) => {
    const userProfile = await getUserProfileStatus()
    const screenList = getScreenList({
      shouldSkipFlow: userProfile.shouldSkipFlow || autoLoginSuccess,
      userProfile,
    })
    const index = screenList.indexOf(key)
    // for mca, we need to return the last screen in the screen list
    if (sku === 'mca') {
      return index > 0 ? screenList[index - 1] : ScreenKey.WELCOME
    }

    if (index > 0) {
      const newKey = screenList[index - 1]

      // if the user skipped to the scheduled time, and then back to the insurance home address, we need to set the skipToScheduledTime to false
      if (skipToScheduledTime && newKey === ScreenKey.SCHEDULED_TIME) {
        setSkipToScheduledTime(false)
        return ScreenKey.INSURANCE_HOME_ADDRESS
      }
      if (IgnoredKey.includes(newKey)) {
        return await getBackKey(newKey)
      } else {
        return newKey
      }
    } else if (key === ScreenKey.UNAVAILABLE) {
      if (skipToScheduledTime) {
        setSkipToScheduledTime(false)
        const scheduledTimeIndex = screenList.indexOf(ScreenKey.SCHEDULED_TIME)
        if (scheduledTimeIndex > 0) {
          return screenList[scheduledTimeIndex - 1]
        } else {
          return ScreenKey.INSURANCE_HOME_ADDRESS
        }
      } else {
        switch (unavailableStatus) {
          case UnavailableStatus.LIMITED_AVAILABILITY_SURVEY:
            return ScreenKey.SURVEY

          case UnavailableStatus.LIMITED_AVAILABILITY_HORMONAL_AUTOIMMUNE:
            return ScreenKey.HORMONAL_AUTOIMMUNE

          case UnavailableStatus.NOT_ELIGIBLE_TALK_ABOUT_COVERAGE:
            return ScreenKey.TALK_ABOUT_COVERAGE

          case UnavailableStatus.LIMITED_AVAILABILITY_SCHEDULED_TIME:
          case UnavailableStatus.NOT_ELIGIBLE_ADDRESS:
            return ScreenKey.INSURANCE_HOME_ADDRESS

          case UnavailableStatus.NOT_ELIGIBLE_CONGRATULATIONS:
            return ScreenKey.SCHEDULED_TIME

          case UnavailableStatus.COMING_SOON_STATE:
          case UnavailableStatus.NOT_ELIGIBLE_STATE:
            return ScreenKey.PERSONAL_INFO

          case UnavailableStatus.AUTO_LOGIN_STATE_UNAVAILABLE:
            setAutoLoginSuccess(false)
            return ScreenKey.WELCOME

          default:
            const scheduledTimeIndex = screenList.indexOf(
              ScreenKey.SCHEDULED_TIME
            )
            if (scheduledTimeIndex > 0) {
              return screenList[scheduledTimeIndex - 1]
            } else {
              return ScreenKey.INSURANCE_HOME_ADDRESS
            }
        }
      }
    } else if (key === ScreenKey.UH_OH) {
      switch (uhOhStatus) {
        case UhOhStatus.PAIN_LOCATED:
          return ScreenKey.PAIN_LOCATED
        case UhOhStatus.QUESTION_1:
          return ScreenKey.REHAB_QUESTION_1
        case UhOhStatus.PAST_DIAGNOSIS:
          return ScreenKey.PAST_DIAGNOSIS
        default:
          return ScreenKey.WELCOME
      }
    } else {
      const list = getScreenList()
      const index = list.indexOf(key)
      if (index > 0) {
        return list[index - 1]
      } else {
        switch (key) {
          case ScreenKey.INSURANCE_NOT_ACCEPTED:
          case ScreenKey.CASH_PAY:
            return ScreenKey.TALK_ABOUT_COVERAGE
          default:
            return ScreenKey.WELCOME
        }
      }
    }
  }

  const onBack = async (e) => {
    e?.stopPropagation()
    const newKey = await getBackKey(currentKey)
    setNewScreenKey(newKey, true)
  }

  useEffect(() => {
    if (variant === 'msk') {
      setIsLoadingTheme(true)
      getSkuThemesPromise({ sku: 'msk' })
        .then((resp) => {
          const themeDetail = resp?.themes?.find((item) => item.name === theme)
          if (themeDetail) {
            setRehabSelectedThemes([themeDetail])
          }
        })
        .catch((e) => {
          setRehabSelectedThemes([])
        })
        .finally(() => {
          setIsLoadingTheme(false)
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, theme])

  const showLoading = useMemo(() => {
    return autoLoginLoading || isLoadingTheme || isAutoLoginDataLoading
  }, [autoLoginLoading, isLoadingTheme, isAutoLoginDataLoading])

  useEffect(() => {
    getStateListPromise()
      .then((resp) => {
        setStateList(resp?.stateList || [])
      })
      .catch((e) => {
        console.error(e)
        setStateList([])
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    currentKey,
    setNewScreenKey,
    autoLoginLoading,
    email,
    utmCampaign,
    utmContent,
    utmSource,
    onBack,
    onLoginSuccess,
    getHealthieUser,
    promo,
    sku,
    fitonUserId,
    theme,
    programId,
    from,
    showLoading,
    gender,
    shouldSkipScheduledTime,
    autoLoginSuccess,
    handleUserAutoLogin,
  }
}
